import { memo, useCallback, useEffect, useRef, useMemo } from "react";

import { LayoutNesting } from "#modules/layouts/infra/LayoutNesting.dto";
import { Alert } from "#shared/view/components/Alert";
import { getSkeletonLoss, isBarOrProfile } from "shared";

import { LAYOUT_COLORS } from "../domain/layoutCanvasConstants";

type Props = {
  width: number;
  height: number;
  data: LayoutNesting | undefined;
  panelId: string;
};

export const LayoutCanvas = memo(({ width, height, data, panelId }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const skeletonLoss = useMemo(() => {
    if (!data) {
      return 0;
    }
    return data.partType ? getSkeletonLoss(data.partType, data.skeletonWidth) : data.skeletonWidth;
  }, [data]);

  const drawLayout = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (!ctx || !canvas || !data) {
      return;
    }

    const panel = data.panels.find((p) => p.name === panelId);
    if (!panel) {
      return;
    }

    // Panel dimensions from backend already have skeleton width subtracted (2x on each dimension)
    // These are the actual drawable dimensions
    const panelWidth = panel.width;
    const panelHeight = panel.height;

    // Calculate scale to fit the panel within the available canvas width
    // Panel dimensions from backend already exclude skeleton width
    // We need to add it back for total sheet size calculation
    const totalPanelWidth = panelWidth + skeletonLoss;
    const totalPanelHeight = panelHeight + skeletonLoss;

    // Use available space with some padding for visual clarity
    const padding = 40;
    const maxAvailableWidth = width - padding;
    const maxAvailableHeight = height - padding;

    const scale = Math.min(
      maxAvailableWidth / totalPanelWidth,
      maxAvailableHeight / totalPanelHeight,
    );

    // Calculate actual canvas dimensions based on scaled panel
    const canvasWidth = panelWidth * scale;
    const canvasHeight = panelHeight * scale;

    // Set canvas size
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Filter items for current panel
    const panelItems = data.items.filter((item) => item.panel === panelId);

    // Draw each item
    panelItems.forEach((item) => {
      const itemWidth = item.width;
      const itemHeight = item.height;

      // Scale coordinates and dimensions
      const x = item.x * scale;
      const y = item.y * scale;
      const w = itemWidth * scale;
      const h = itemHeight * scale;

      // Draw rectangle
      ctx.fillStyle = item.type === "used" ? LAYOUT_COLORS.USED_ITEM : LAYOUT_COLORS.UNUSED_ITEM;
      ctx.fillRect(x, y, w, h);

      // Draw border
      ctx.strokeStyle = LAYOUT_COLORS.BORDER;
      ctx.lineWidth = 0.5;
      ctx.strokeRect(x, y, w, h);

      // Draw text if item is large enough
      const minDimension = Math.min(w, h);
      if (minDimension > 20) {
        const fontSize = Math.max(8, Math.min(minDimension / 4, 16));
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        const itemArea = item.width * item.height;
        const sheetArea = totalPanelWidth * totalPanelHeight;
        const percentage = (itemArea / sheetArea) * 100;

        if (item.type === "used") {
          ctx.fillStyle = LAYOUT_COLORS.TEXT_ON_USED;
          ctx.fillText(`${percentage.toFixed(1)}%`, x + w / 2, y + h / 2);
        }

        if (item.type === "unused") {
          ctx.fillStyle = LAYOUT_COLORS.TEXT_ON_UNUSED;
          ctx.fillText(`${percentage.toFixed(1)}%`, x + w / 2, y + h / 2);
        }
      }
    });
  }, [data, panelId, width, height, skeletonLoss]);

  useEffect(() => {
    if (data && canvasRef.current) {
      drawLayout();
    }
  }, [data, drawLayout]);

  if (!data) {
    return null;
  }

  const panel = data.panels.find((p) => p.name === panelId);
  if (!panel) {
    return <Alert color="orange">Erreur: Panel introuvable</Alert>;
  }

  const totalPanelWidth = panel.width + skeletonLoss;
  const totalPanelHeight = panel.height + skeletonLoss;

  const padding = 40;
  const maxAvailableWidth = width - padding;
  const maxAvailableHeight = height - padding;

  const scale = Math.min(
    maxAvailableWidth / totalPanelWidth,
    maxAvailableHeight / totalPanelHeight,
  );

  const displayWidth = panel.width * scale;
  const displayHeight = panel.height * scale;
  const scaledSkeletonWidth = data.skeletonWidth * scale;

  const isBarOrProfileLayout = isBarOrProfile(data.partType);
  const horizontalPadding = scaledSkeletonWidth;
  const verticalPadding = isBarOrProfileLayout ? 0 : scaledSkeletonWidth;

  return (
    <div
      style={{
        position: "relative",
        width: displayWidth,
        height: displayHeight,
        backgroundColor: LAYOUT_COLORS.CANVAS_BACKGROUND,

        margin: "0 auto",
        padding: `${verticalPadding}px ${horizontalPadding}px`,
        boxSizing: "content-box",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
        background: `
            repeating-linear-gradient(
              45deg,
              #1a1a1a,
              #1a1a1a 5px,
              #222 5px,
              #222 10px
            )
          `,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          backgroundColor: "white",
        }}
      />
    </div>
  );
});

LayoutCanvas.displayName = "LayoutCanvas";
